// Permission types matching Casdoor RBAC resource:action pairs
export type Permission =
  // Device
  | 'device:read'
  | 'device:write'
  // Command
  | 'command:read'
  | 'command:reboot'
  | 'command:lock'
  | 'command:factory_reset'
  // Firmware
  | 'firmware:read'
  | 'firmware:upload'
  // Rollout
  | 'rollout:read'
  | 'rollout:create'
  // Log
  | 'log:read'
  | 'log:export'
  // Audit
  | 'audit:read'
  // Config
  | 'config:read'
  | 'config:write'
  // Resource
  | 'resource:read'
  | 'resource:write'
  // User
  | 'user:read'
  | 'user:write'
  | 'user:delete'
  | 'user:manage_roles'
  // System (wildcard)
  | 'system:admin';

// Tuple-based permission (matches backend domain.PermissionTuple)
export interface PermissionTuple {
  resource: string;
  action: string;
}

// Check if a user has a specific permission
export function canDo(permissions: PermissionTuple[] | undefined, resource: string, action: string): boolean {
  if (!permissions) return false;
  return permissions.some(
    (p) =>
      (p.resource === '*' || p.resource === resource) &&
      (p.action === '*' || p.action === action)
  );
}

// Check if user has any of the given permissions
export function canDoAny(permissions: PermissionTuple[] | undefined, checks: PermissionTuple[]): boolean {
  if (!permissions) return false;
  return checks.some((check) => canDo(permissions, check.resource, check.action));
}

// Check if user has admin (wildcard) permissions
export function isAdmin(permissions: PermissionTuple[] | undefined): boolean {
  return canDo(permissions, '*', '*');
}

// Check if user has super admin role (role-based, NOT permission-based)
export function isSuperAdmin(user: { role: string } | null): boolean {
  if (!user) return false;
  return user.role === 'super_admin';
}

// Role display info (roles are managed in Casdoor, not in DB)
export interface RoleInfo {
  name: string;
  displayName: string;
  description: string;
}

// User types
export interface User {
  id: string;
  email: string;
  name: string;
  display_name?: string;
  avatar?: string;
  organization: string;
  role: string; // Mapped role (super_admin, admin, manager, support, user)
  timezone?: string;
  permissions?: PermissionTuple[];
  legacy_permissions?: string[];
  created_at?: string;
  updated_at?: string;
}

// Auth types
export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  user: User;
}

export interface LoginResponse {
  login_url: string;
}

// Device types (MDM)
export type DeviceStatus = 'pending' | 'online' | 'offline' | 'blocked' | 'away';

export interface StorageInfo {
  total_bytes: number;
  used_bytes: number;
  free_bytes: number;
}

export interface NetworkInfo {
  type: string;
  ssid: string;
  ip_address: string;
  mac_address: string;
  signal_strength: number;
}

export interface DeviceInfo {
  imei: string;
  mac_address: string;
  cpu: string;
  memory_total: number;
  storage: StorageInfo;
  network: NetworkInfo;
}

export interface Device {
  id: string;
  tenant_id: string;
  machine_id?: string;  // MAC-based hardware ID used in MQTT
  serial_number: string;
  number: string;
  description: string;
  type: string;
  sub_type: string; // Treadmill submodel (MachineSubType 0-4); "" / "0" = NONE
  // DeviceInfo 1.6 — how the machine controller is wired (DeviceProtocol
  // 0 NONE / 1 UART / 2 HID / 255 UNKNOWN). "" on firmware that predates it.
  // Distinct from the MQTT wire encoding.
  device_protocol?: string;
  model: string;
  manufacturer: string;
  os_version: string;
  android_version: string;
  agent_version: string;
  rom_version: string;
  status: DeviceStatus;
  configuration_id?: string;
  public_ip: string;
  info: DeviceInfo;
  // Persisted from the most recent DeviceInfo.getMDMVersion call.
  // Shape matches the interface v1.4 response.
  mdm_version_info?: {
    version?: string;
    fullVersion?: string;
    versionCode?: number;
    buildDate?: string;
    gitCommit?: string;
    gitDirty?: boolean;
  };
  enrolled_at?: string;
  last_online?: string;
  // First-class ownership flag: true = Aviron-internal device, false = customer.
  // Drives the InternalDeviceIcon marker and the device-list ownership filter.
  is_internal: boolean;
  created_at: string;
  updated_at: string;
}

// Stable category identifier assigned by the backend package catalog. The UI
// must not infer categories from package names or types.
export type PackageCategory = 'platform' | 'system_app' | 'streaming_app';

// Unified software inventory snapshot from GET /devices/:id/software-versions.
// Backend-stored only (refreshed server-side after install-complete / reboot
// install-success); the UI never triggers a refresh. Note: no full_version.
// The list is the complete inventory (MDM Agent + every queryable package);
// version is null when not installed / not reported / unavailable.
export interface DeviceSoftwareVersion {
  display_name: string;
  package_name: string;
  type: 'mdm' | 'app' | 'os' | 'fw' | string;
  category: PackageCategory;
  version: string | null;
  // Present in the response but not surfaced in the UI (not reliably populated).
  updated_at?: string;
}

export interface DeviceSoftwareVersionsResponse {
  device_id: string;
  versions: DeviceSoftwareVersion[];
}

export interface DeviceFilter {
  tenant_id?: string;
  status?: DeviceStatus;
  group_id?: string;
  // Multi-value filters — serialized to the API as comma-separated params.
  type?: string[];
  sub_type?: string[];
  model?: string[];
  os_version?: string[];
  agent_version?: string[];
  rom_version?: string[];
  // Ownership filter — serialized as the `is_internal` bool query param.
  // undefined omits it (the "All" case).
  is_internal?: boolean;
  last_seen?: string; // "1h", "24h", "7d", "30d", "never", "inactive_7d", "inactive_30d"
  // Personal favorites. `true` narrows to machines the caller has starred;
  // undefined omits the param. There is no "only unstarred" case, so this is a
  // two-state flag rather than the is_internal-style tri-state.
  favorite?: boolean;
  search?: string;
  sort?: string; // "" | created_desc | created_asc | last_seen_desc | number_asc | favorites_first
  limit?: number;
  offset?: number;
}

/**
 * One personal star. Private to the signed-in user — no endpoint accepts a
 * subject parameter, so there is no way to read anyone else's.
 *
 * Keyed on `machine_id` (the hardware) rather than a device UUID, so a star
 * survives its device row being deleted and re-attaches if the same machine
 * re-enrolls. When that happens the favorite is "dormant": `device_id` and the
 * display fields come back empty, and there is nothing to link to.
 */
export interface DeviceFavorite {
  machine_id: string;
  device_id?: string;
  number?: string;
  serial_number?: string;
  status?: string;
  created_at: string;
}

export interface DeviceListResponse {
  devices: Device[];
  total: number;
}

export interface DeviceFilterOptions {
  types: string[];
  sub_types: string[];
  models: string[];
  os_versions: string[];
  agent_versions: string[];
  rom_versions: string[];
  /** "internal" / "customer", derived from is_internal so each side can be counted. */
  ownership: string[];
  /**
   * Values that exist in the tenant but match nothing under the current
   * filter. They stay in the lists above and are rendered greyed: a facet
   * whose options disappear as you filter reads as the data shifting, and an
   * applied value could be narrowed out of its own facet with no way to
   * untick it.
   */
  unavailable?: Omit<DeviceFilterOptions, 'unavailable' | 'counts'>;
  /**
   * How many devices each value matches under the current filter, with that
   * facet's own clause skipped. The facet you group BY comes back fleet-wide
   * while every other facet is scoped to the selection, so one request drives
   * a whole drill-down.
   */
  counts?: DeviceFacetCounts;
}

export interface DeviceBreakdownCell {
  row: string;
  col: string;
  count: number;
}

export interface DeviceBreakdownResponse {
  rows: string;
  cols: string;
  cells: DeviceBreakdownCell[];
}

export interface DeviceFacetCounts {
  types: Record<string, number>;
  sub_types: Record<string, number>;
  models: Record<string, number>;
  os_versions: Record<string, number>;
  agent_versions: Record<string, number>;
  rom_versions: Record<string, number>;
  ownership: Record<string, number>;
  tags: Record<string, number>;
}

export interface DeviceStats {
  total: number;
  online: number;
  offline: number;
  pending: number;
  blocked: number;
  away?: number;
}

// Device Log types
export type LogType = 'system' | 'app' | 'crash' | 'network' | 'security' | 'command' | 'activity' | 'kernel';
export type LogLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

export interface DeviceLog {
  id: string;
  device_id: string;
  log_type: LogType;
  level: LogLevel;
  message: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface DeviceLogFilter {
  log_type?: LogType;
  level?: LogLevel;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface DeviceLogListResponse {
  logs: DeviceLog[];
  total: number;
  limit: number;
  offset: number;
}

// Command types
export type CommandType =
  | 'lock'
  | 'unlock'
  | 'reboot'
  | 'clear_cache'
  | 'factory_reset'
  | 'wipe'
  | 'push_config'
  | 'install_app'
  | 'uninstall_app'
  | 'fetch_logs'
  | 'run_command'
  | 'screenshot';

export type CommandStatus =
  | 'pending'
  | 'sent'
  | 'executed'
  | 'failed'
  | 'cancelled'
  | 'timed_out';

export interface Command {
  id: string;
  device_id: string;
  type: CommandType;
  payload: Record<string, unknown>;
  status: CommandStatus;
  result: string;
  error?: string;
  created_by?: string;
  created_at: string;
  sent_at?: string;
  executed_at?: string;
}

export interface CreateCommandRequest {
  type: CommandType;
  payload?: Record<string, unknown>;
}

// Live Logging types
export interface EnableLiveLoggingRequest {
  level?: LogLevel;
  patchSize?: number;
  patchTimeout?: number;
}

export interface LiveLoggingResponse {
  success: boolean;
  message: string;
  deviceId: string;
  config?: {
    level: string;
    patchSize: number;
    patchTimeout: number;
  };
}

// Memory Monitor types
export interface EnableMemoryMonitorRequest {
  intervalMs?: number;  // Default: 5000
  threshold?: number;   // Default: 102400 (100MB in KB)
}

export interface MemoryMonitorResponse {
  success: boolean;
  message: string;
  deviceId: string;
  config?: {
    intervalMs: number;
    threshold: number;
  };
}

export interface MemoryReport {
  totalKb: number;
  usedKb: number;
  freeKb: number;
  availableKb: number;
  timestamp: string;
}

// Configuration types
export interface ConfigurationSettings {
  kiosk_mode: boolean;
  allowed_apps: string[];
  blocked_apps: string[];
  wifi_config: Record<string, unknown>;
  restrictions: Record<string, unknown>;
}

export interface Configuration {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  settings: ConfigurationSettings;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

// Common API response types
export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, string>;
}

// Table types
export interface SortingState {
  id: string;
  desc: boolean;
}

export interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

// Form types
export interface SelectOption {
  label: string;
  value: string;
}

// Dashboard stats
export interface DashboardStats {
  totalDevices: number;
  activeDevices: number;
  pendingDevices: number;
  blockedDevices: number;
}

// Device Status Event (for realtime updates)
export type DeviceActivityState = 'online' | 'away' | 'offline';

export interface DeviceStatusEvent {
  device_id: string;
  tenant_id: string;
  status: DeviceStatus;
  device_state?: DeviceActivityState;
  ip_address?: string;
  cpu_percent?: number;
  memory_kb?: number;
  timestamp: string;
}

/** Which device-global stream a FeatureSessionEvent refers to. */
export type FeatureType = 'live_logging' | 'memory_monitor' | 'activity_monitor';

/**
 * A device-global feature session started or stopped. Pushed over the device
 * WebSocket as `{"type":"feature_session",...}`, bridged from the backend's
 * Redis "feature:updates" channel.
 *
 * These sessions belong to the device, not to the tab that started one — so a
 * Stop in another tab, a session TTL lapsing, or the device dropping offline
 * all end the stream this tab is watching. Without this event that just looks
 * like a feed that stopped updating.
 */
export interface FeatureSessionEvent {
  event: 'enabled' | 'disabled';
  device_id: string;
  tenant_id?: string;
  feature_type: FeatureType;
  enabled_by?: string;
  /** Currently always "admin" or "system" server-side — not yet the operator's
   *  identity, so don't word UI copy as if it named a person. */
  disabled_by?: string;
  /** manual | expired | ws_disconnect | device_offline | server_shutdown.
   *  Treat anything unrecognised as a generic stop. */
  reason?: string;
  /** True only on the same-browser BroadcastChannel path, where we know the
   *  stop came from another tab of this browser. The server never sets it, so
   *  a server-relayed stop stays worded as "another session" — it could be a
   *  different browser or a different operator. */
  local?: boolean;
  timestamp: string;
}

// Diagnostic v1.1 types
export interface RebootRequest {
  delayMs?: number;
}

export interface RebootResponse {
  success: boolean;
  deviceId: string;
  delayMs: number;
  response?: Record<string, unknown>;
}

export interface StorageInfoResponse {
  success: boolean;
  deviceId: string;
  response?: {
    internal?: { total_bytes: number; used_bytes: number; free_bytes: number };
    external?: { total_bytes: number; used_bytes: number; free_bytes: number };
  };
}

// Shell command types
export interface ShellCommandRequest {
  command: string;
  timeoutMs?: number;
}

export interface ShellCommandResponse {
  success: boolean;
  deviceId: string;
  command: string;
  response?: {
    exitCode?: number;
    stdout?: string;
    stderr?: string;
  };
}

// Filesystem listing — backs the Disk Usage Analyzer (Diagnostic v1.5).
// Sizes are NOT included in list responses — the UI calls /filesystem/size
// per row after listing arrives so a slow recursive walk on one entry
// can't time out the whole view.
export interface FilesystemEntry {
  name: string;
  isDirectory: boolean;
  permission: number;
}

export interface ListFilesystemResponse {
  success: boolean;
  deviceId: string;
  path: string;
  entries: FilesystemEntry[];
}

export interface GetItemSizeResponse {
  success: boolean;
  deviceId: string;
  path: string;
  sizeBytes: number;
}

// Custom error class the deviceService throws when the backend reports
// HTTP 501 with code: "firmware_outdated". The UI renders a dedicated
// "update firmware" state instead of a generic error toast.
export class FirmwareOutdatedError extends Error {
  readonly code = 'firmware_outdated';
  readonly minVersion: string;
  constructor(message: string, minVersion: string) {
    super(message);
    this.name = 'FirmwareOutdatedError';
    this.minVersion = minVersion;
  }
}

// Canonical action names. Mirror domain.Action* constants on the
// backend — stored as "namespace.verb" so the audit log groups
// cleanly. The UI maps these to short display labels at render time
// (see actionLabel in pages/device-detail.tsx); storage and API stay
// canonical.
export const ACTION_SHELL_EXECUTE = 'shell.execute';
export const ACTION_DEVICE_FACTORY_RESET = 'device.factory_reset';
export const ACTION_DEVICE_REBOOT = 'device.reboot';
export const ACTION_DEVICE_DELETE = 'device.delete';
export const ACTION_DEVICE_GET_INFO = 'device.get_info';
export const ACTION_SCREENSHOT_CAPTURE = 'screenshot.capture';
export const ACTION_SCREENSHOT_DELETE = 'screenshot.delete';
export const ACTION_LOG_FORCE_UPLOAD = 'log.force_upload';
export const ACTION_LOG_DOWNLOAD = 'log.download';
export const ACTION_LOG_ARCHIVE_DOWNLOAD = 'log.archive_download';
export const ACTION_LOG_SYNC_GCS = 'log.sync_gcs';
export const ACTION_FILESYSTEM_LIST = 'filesystem.list';
export const ACTION_COMMAND_SEND = 'command.send';
export const ACTION_COMMAND_CANCEL = 'command.cancel';
export const ACTION_MONITOR_LIVE_LOGGING_ENABLE = 'monitor.live_logging.enable';
export const ACTION_MONITOR_LIVE_LOGGING_DISABLE = 'monitor.live_logging.disable';
export const ACTION_MONITOR_MEMORY_ENABLE = 'monitor.memory.enable';
export const ACTION_MONITOR_MEMORY_DISABLE = 'monitor.memory.disable';
export const ACTION_MONITOR_ACTIVITY_ENABLE = 'monitor.activity.enable';
export const ACTION_MONITOR_ACTIVITY_DISABLE = 'monitor.activity.disable';
export const ACTION_QUICKLOGIN_DELETE = 'quicklogin.delete';

export type DeviceActionResult = 'pending' | 'success' | 'failure';

// Unified per-device audit row. Replaces the old ShellCommandAudit
// shape — every operator action lives here, discriminated by `action`.
// Shell-specific fields (command, stdout, stderr, exit_code) move into
// params/result_data so the table can carry any action shape.
export interface DeviceActionAudit {
  id: string;
  device_id: string;
  tenant_id: string;
  action: string;
  params?: Record<string, unknown>;
  result_data?: Record<string, unknown>;
  result: DeviceActionResult;
  error?: string;
  executed_by: string;
  /** Initiator email captured when the action ran. Absent on rows written
   *  before the backend started snapshotting it, and on system actions. */
  executed_by_email?: string;
  executed_at: string;
  completed_at?: string;
}

export interface DeviceActionAuditsResponse {
  deviceId: string;
  audits: DeviceActionAudit[];
  total: number;
  limit: number;
  offset: number;
}

// Fleet Analytics types
export interface VersionCount {
  version: string;
  device_count: number;
}

export interface VersionDistributionResponse {
  type: string;
  distribution: VersionCount[];
}

// Uptime types
export interface DeviceUptimeResult {
  device_id: string;
  tenant_id: string;
  start_time: string;
  end_time: string;
  uptime_percent: number;
  /** time.Duration serialised as nanoseconds by Go's json encoder. */
  total_online_nanos: number;
  total_offline_nanos: number;
  session_count: number;
}

export interface DeviceUptimeBrief {
  /** Analytics key (machine_id). */
  device_id: string;
  /** Postgres UUID — present when the roster join resolved; navigable to /devices/{id}. */
  id?: string;
  /** Device name from the roster, when resolved. */
  name?: string;
  /** Ownership flag from the roster join; false when unresolved. */
  is_internal: boolean;
  uptime_percent: number;
  session_count: number;
}

export interface FleetUptimeResult {
  tenant_id: string;
  start_time: string;
  end_time: string;
  avg_uptime_percent: number;
  total_devices: number;
  devices?: DeviceUptimeBrief[];
}

// Heartbeat cadence types (mirror Go domain.HeartbeatCadenceResult etc.)
export interface CadenceBucket {
  timestamp: string;
  beat_count: number;
  expected_beats: number;
  missed_beats: number;
  avg_interval_seconds: number;
  max_interval_seconds: number;
}

export interface HeartbeatCadenceResult {
  device_id: string;
  tenant_id: string;
  start_time: string;
  end_time: string;
  nominal_interval_seconds: number;
  total_beats: number;
  avg_interval_seconds: number;
  max_interval_seconds: number;
  coverage_percent: number;
  gap_count: number;
  /** true when the device beats faster than 4x nominal — interval stats are zeroed */
  degraded: boolean;
  buckets: CadenceBucket[];
}

/** device_id here is the analytics key (machine_id), NOT the postgres UUID */
export interface DeviceHeartbeatBrief {
  device_id: string;
  beat_count: number;
  expected_beats: number;
  coverage_percent: number;
}

export interface FleetHeartbeatResult {
  tenant_id: string;
  start_time: string;
  end_time: string;
  total_devices: number;
  avg_coverage_percent: number;
  devices?: DeviceHeartbeatBrief[];
}

export interface DeviceCadenceParams {
  start?: string;
  end?: string;
  interval?: string;
}

// Fleet analytics dashboard types (mirror Go domain/fleet_analytics.go)
export interface FleetTrendBucket {
  timestamp: string;
  online_devices: number;
  avg_cpu_percent: number;
  p95_cpu_percent: number;
  avg_memory_kb: number;
  p95_memory_kb: number;
  sample_count: number;
}

export interface FleetMetricsTrendResult {
  tenant_id: string;
  start_time: string;
  end_time: string;
  buckets: FleetTrendBucket[];
}

export interface EventTrendBucket {
  timestamp: string;
  count: number;
}

/** device_id is the analytics key (machine_id); id/name are filled when the roster join resolves. */
export interface DeviceEventCount {
  device_id: string;
  /** Postgres UUID — present when the roster join resolved; navigable to /devices/{id}. */
  id?: string;
  /** Device name from the roster, when resolved. */
  name?: string;
  /** Ownership flag from the roster join; false when unresolved. */
  is_internal: boolean;
  count: number;
}

export type FleetEventType = 'boot' | 'presence_online' | 'presence_offline';

export interface FleetEventStatsResult {
  tenant_id: string;
  event_type: string;
  start_time: string;
  end_time: string;
  total_events: number;
  buckets: EventTrendBucket[];
  top_devices: DeviceEventCount[];
}

export interface FleetDeviceHealth {
  id: string; // postgres UUID — navigable to /devices/{id}
  machine_id: string;
  name: string;
  is_internal: boolean; // ownership flag from the roster join
  beat_count: number;
  expected_beats: number; // enrollment-clamped
  coverage_percent: number;
}

export interface SilentDevice {
  id: string; // postgres UUID — navigable
  machine_id: string;
  name: string;
  is_internal: boolean; // ownership flag from the roster join
  enrolled_at: string | null;
  last_online: string | null;
  silent_for_seconds: number;
}

export interface FleetHealthResult {
  tenant_id: string;
  start_time: string;
  end_time: string;
  total_devices: number;
  reporting_devices: number;
  silent_count: number;
  silent_devices: SilentDevice[];
  avg_coverage_percent: number;
  worst_devices: FleetDeviceHealth[];
}

export interface EnrollmentWeek {
  week_start: string;
  new_devices: number;
}

export interface EnrollmentGrowthResult {
  tenant_id: string;
  start_time: string;
  end_time: string;
  total: number;
  weeks: EnrollmentWeek[];
}


// Device Log File types
export type DeviceLogFileStatus = 'available' | 'unavailable';

export interface DeviceLogFile {
  id: string;
  device_id: string;
  tenant_id: string;
  file_name: string;
  file_size: number;
  log_type: string | null;
  status: DeviceLogFileStatus;
  uploaded_at: string;
  created_at: string;
}

export interface DeviceLogFileFilter {
  search?: string;
  logType?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface DeviceLogFileSyncResponse {
  device_id: string;
  status: string;
}

export interface SyncJobStatus {
  status: 'idle' | 'starting' | 'syncing' | 'done' | 'error';
  device_id?: string;
  synced?: number;
  skipped?: number;
  total?: number | null;
  truncated?: boolean;
  error?: string;
}

export interface DeviceLogFilesResponse {
  files: DeviceLogFile[];
  total: number;
  limit: number;
  offset: number;
}

export interface DeviceLogFileStats {
  total_files: number;
  total_size: number;
  oldest_file: string | null;
  newest_file: string | null;
}

// Auth Audit types
export interface AuthAuditEvent {
  id: string;
  user_id: string;
  email: string;
  tenant_id: string;
  event_type: string;
  resource?: string;
  action?: string;
  ip_address: string;
  user_agent: string;
  success: boolean;
  details?: Record<string, unknown>;
  created_at: string;
}

export interface AuthAuditFilter {
  user_id?: string;
  event_type?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

export interface AuthAuditListResponse {
  data: AuthAuditEvent[];
  total: number;
}

// Device Analytics types (time-series from ClickHouse)
export interface DeviceMetricPoint {
  device_id: string;
  tenant_id: string;
  cpu_percent: number;
  memory_kb: number;
  timestamp: string;
}

export interface AggregatedMetricPoint {
  timestamp: string;
  avg_cpu_percent: number;
  max_cpu_percent: number;
  avg_memory_kb: number;
  max_memory_kb: number;
  sample_count: number;
}

export interface DeviceMetricsResponse {
  device_id: string;
  interval?: string;
  data: DeviceMetricPoint[] | AggregatedMetricPoint[] | null;
  count: number;
}

export interface DeviceEventItem {
  device_id: string;
  tenant_id: string;
  event_type: string;
  event_data: Record<string, unknown> | null;
  timestamp: string;
}

export interface DeviceEventsResponse {
  device_id: string;
  data: DeviceEventItem[] | null;
  total: number;
  count: number;
}

export interface DeviceMetricsParams {
  start?: string;
  end?: string;
  interval?: string;
  limit?: number;
}

export interface DeviceEventsParams {
  start?: string;
  end?: string;
  type?: string;
  limit?: number;
  offset?: number;
}

// Screen Capture types
export interface Screenshot {
  id: string;
  device_id: string;
  tenant_id: string;
  storage_key: string;
  file_size: number;
  // captured_at = when the capture command was issued (request time).
  // uploaded_at = when the GCS object actually landed (verified via
  // HEAD/ACK). null while file_size = 0 (still pending) or for legacy
  // rows captured before the column existed. The gallery prefers
  // uploaded_at for display so late uploads show their truthful arrival
  // time instead of the now-stale request time.
  captured_at: string;
  uploaded_at?: string | null;
  created_at: string;
  download_url?: string;
  // Linked mdm.device_commands.id — populated for captures dispatched
  // through the command-lifecycle service (FU2). NULL for historical
  // rows; detail panel hides the lifecycle timeline in that case.
  command_id?: string | null;
}

// DeviceCommand is the typed projection of mdm.device_commands used by
// the screen-capture detail panel's lifecycle timeline. Wire shape
// matches internal/domain/device_command.go on the backend.
export interface DeviceCommandStateTransition {
  state: string;
  at: string;
  detail?: string;
}

export interface DeviceCommandInitiator {
  user_id?: string;
  source?: string;
  automation_id?: string;
}

export interface DeviceCommand {
  id: string;
  device_id: string;
  tenant_id: string;
  command_type: string;
  state: 'queued' | 'dispatched' | 'executing' | 'waiting_user' | 'succeeded' | 'failed' | 'timeout' | 'cancelled';
  payload?: unknown;
  result?: unknown;
  initiator?: DeviceCommandInitiator;
  state_history?: DeviceCommandStateTransition[];
  error_code?: number;
  error_reason?: string;
  request_id?: string;
  late_ack_received_at?: string | null;
  created_at: string;
  updated_at: string;
  version: number;
}

export interface ScreenshotListResponse {
  screenshots: Screenshot[];
  total: number;
  limit: number;
  offset: number;
}

export interface ScreenshotFilter {
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface CaptureScreenshotResponse {
  success: boolean;
  device_id: string;
  screenshot_id?: string;
  // Populated when the backend routes capture through the command
  // lifecycle (FU2). The frontend uses it to subscribe to
  // /ws/devices/:id/commands for live state transitions on the
  // in-flight capture (drives the "Capturing screen…" live panel).
  command_id?: string;
  message?: string;
}

// Log archive (GCS-scan path) — surfaces files discovered directly in the
// configured archive buckets, separate from the DB-tracked log_files flow.
export interface LogArchiveObject {
  bucket: string;
  name: string;
  size: number;
  updated: string;        // RFC3339 — GCS-side write timestamp (authoritative)
  date_from_name: string; // RFC3339, parsed from filename — may be zero-time if unparseable
}

export interface LogArchiveDateCount {
  date: string; // RFC3339
  count: number;
}

export interface LogArchiveListResponse {
  objects: LogArchiveObject[];
  counts: LogArchiveDateCount[];
  total_bytes: number;
  max_range_days?: number;     // server-side cap on (to - from); 0 = unlimited
  buckets?: string[];          // every configured archive bucket name — use for filter UIs
  log_upload_bucket?: string;  // GCS bucket the mdm log_upload pipeline writes to — label for tracked-only rows
}

export interface LogArchiveDownloadURLResponse {
  url: string;
  bucket: string;
  name: string;
  expires_in: number; // seconds
}

// Navigation
export interface NavItem {
  title: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
  badge?: number;
  children?: NavItem[];
}
